import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatEuro } from "@/lib/api";
import { ScadenzaBadge, StatoBadge } from "@/components/Badges";
import { Banknote, Wallet, AlertTriangle, FilePlus2, ArrowRight, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white border border-[#E5E2DC] rounded-xl p-6 shadow-sm"
    data-testid={testid}
  >
    <div className="flex items-center justify-between mb-4">
      <span className="text-xs tracking-[0.15em] uppercase text-[#5C5F58]">{label}</span>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
      </div>
    </div>
    <p className="font-heading text-3xl font-bold text-[#1A1C18]">{value}</p>
  </motion.div>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recenti, setRecenti] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.stats().then(setStats).catch(() => {});
    api.lista().then((d) => setRecenti(d.slice(0, 5))).catch(() => {});
  }, []);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Cruscotto</h1>
          <p className="text-[#5C5F58] mt-1">Tracciabilità e promemoria dei tuoi assegni bancari</p>
        </div>
        <Button
          onClick={() => navigate("/nuovo")}
          data-testid="dashboard-nuovo-btn"
          className="bg-[#1C3F35] hover:bg-[#132A23] text-white hover:-translate-y-0.5 transition-transform"
        >
          <FilePlus2 className="w-4 h-4 mr-1.5" /> Nuovo Assegno
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard testid="stat-totale" icon={Banknote} label="Assegni totali" value={stats?.totale ?? "—"} accent="bg-[#E8F0E9] text-[#2F5235]" />
        <StatCard testid="stat-valore" icon={Wallet} label="Valore totale" value={stats ? formatEuro(stats.valore_totale) : "—"} accent="bg-[#E8F0E9] text-[#2F5235]" />
        <StatCard testid="stat-scadenza" icon={AlertTriangle} label="In scadenza (≤3g)" value={stats?.in_scadenza_3 ?? "—"} accent="bg-[#FBE3DA] text-[#B84223]" />
        <StatCard testid="stat-girati" icon={ListChecks} label="Girati" value={stats?.per_stato?.girato ?? "—"} accent="bg-[#FBEAD2] text-[#A86616]" />
      </div>

      <div className="bg-white border border-[#E5E2DC] rounded-xl shadow-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E2DC]">
          <h2 className="font-heading text-xl font-semibold">Assegni recenti</h2>
          <button
            onClick={() => navigate("/archivio")}
            data-testid="dashboard-vedi-archivio"
            className="text-sm text-[#1C3F35] font-medium flex items-center gap-1 hover:gap-2 transition-all"
          >
            Vedi archivio <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        {recenti.length === 0 ? (
          <div className="p-10 text-center text-[#5C5F58]">
            Nessun assegno registrato. Inizia caricando la foto di un assegno.
          </div>
        ) : (
          <div className="divide-y divide-[#F2F0EB]">
            {recenti.map((a) => (
              <div
                key={a.id}
                onClick={() => navigate("/archivio")}
                className="px-6 py-4 flex items-center justify-between hover:bg-[#F9F8F6] cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#F2F0EB] flex items-center justify-center">
                    <Banknote className="w-5 h-5 text-[#1C3F35]" strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{a.banca || "Banca n.d."} · N° {a.numero_assegno || "—"}</p>
                    <p className="text-xs text-[#5C5F58]">Da: {a.ricevuto_da || "—"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-mono-num font-semibold text-sm">{formatEuro(a.valore)}</span>
                  <StatoBadge stato={a.stato} />
                  <ScadenzaBadge giorni={a.giorni_alla_scadenza} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
